import React from 'react';
import {
    Switch
} from '@patternfly/react-core';

import { BaseInput, useBaseInput } from './BaseInput';
import { BaseSwitch } from './BaseSwitch';
import { AdvancedOptionsData } from '../types/formTypes';
import { repoBranchValidator } from '../utils/validators';

interface AdvancedOptionsSectionProps {
    formRowStyle: React.CSSProperties;
    onValidationChange?: (isValid: boolean) => void;
    onDataChange?: (data: AdvancedOptionsData) => void;
    layout?: 'table' | 'card';
}

export interface AdvancedOptionsSectionRef {
    validateAdvancedOptions: () => boolean;
    resetAdvancedOptions: () => void;
}



export const AdvancedOptionsSection = React.forwardRef<AdvancedOptionsSectionRef, AdvancedOptionsSectionProps>(({
    formRowStyle,
    onValidationChange,
    onDataChange,
    layout = 'table'
}, ref) => {
    // State to control visibility of advanced options
    const [isExpanded, setIsExpanded] = React.useState(false);

    // Handle all Advanced Options state internally
    const {
        value: templateRepoBranch,
        error: templateRepoBranchError,
        handleChange: handleTemplateRepoBranchChange,
        handleBlur: handleTemplateRepoBranchBlur,
        validate: validateTemplateRepoBranch,
        setError: setTemplateRepoBranchError,
        setValue: setTemplateRepoBranch,
        isValid: isTemplateRepoBranchValid
    } = useBaseInput({
        required: false,
        minLength: 2,
        customValidator: repoBranchValidator
    });

    const {
        value: deployRepoBranch,
        error: deployRepoBranchError,
        handleChange: handleDeployRepoBranchChange,
        handleBlur: handleDeployRepoBranchBlur,
        validate: validateDeployRepoBranch,
        setError: setDeployRepoBranchError,
        setValue: setDeployRepoBranch,
        isValid: isDeployRepoBranchValid
    } = useBaseInput({
        required: false,
        minLength: 2,
        customValidator: repoBranchValidator
    });

    const {
        value: generatorBranchName,
        error: generatorBranchNameError,
        handleChange: handleGeneratorBranchNameChange,
        handleBlur: handleGeneratorBranchNameBlur,
        validate: validateGeneratorBranchName,
        setError: setGeneratorBranchNameError,
        setValue: setGeneratorBranchName,
        isValid: isGeneratorBranchNameValid
    } = useBaseInput({
        required: false,
        minLength: 1,
        customValidator: repoBranchValidator
    });

    // Set default values on component mount
    React.useEffect(() => {
        setDeployRepoBranch('template-poc');
        setGeneratorBranchName('main');
    }, []); // Only run once on mount

    // Skip build job creation switch state
    const [skipBuildJobCreation, setSkipBuildJobCreation] = React.useState<boolean>(false);

    const handleSkipBuildJobCreationChange = React.useCallback((checked: boolean) => {
        setSkipBuildJobCreation(checked);
    }, []);

    // Notify parent of validation status changes
    React.useEffect(() => {
        // Since all advanced options are optional, they're always valid
        // Only check for validation errors if user has entered values
        const isValid = !templateRepoBranchError && !deployRepoBranchError && !generatorBranchNameError;
        onValidationChange?.(isValid);
    }, [templateRepoBranchError, deployRepoBranchError, generatorBranchNameError, onValidationChange]);

    // Notify parent of data changes
    React.useEffect(() => {
        const advancedData: AdvancedOptionsData = {
            templateRepoBranch: templateRepoBranch || undefined,
            deployRepoBranch: deployRepoBranch || undefined, // Always include since we're always 'new'
            generatorBranchName: generatorBranchName || undefined,
            skipBuildJobCreation: skipBuildJobCreation
        };
        onDataChange?.(advancedData);
    }, [templateRepoBranch, deployRepoBranch, generatorBranchName, skipBuildJobCreation, onDataChange]);

    // Expose validation and reset functions for form submission
    React.useImperativeHandle(ref, () => ({
        validateAdvancedOptions: () => {
            // Only validate if user has entered values, since all fields are optional
            if (templateRepoBranch) {
                validateTemplateRepoBranch();
            }
            if (deployRepoBranch) { // Always validate deployRepoBranch since we're always 'new'
                validateDeployRepoBranch();
            }
            if (generatorBranchName) {
                validateGeneratorBranchName();
            }
            // Return true if no errors (since fields are optional)
            return !templateRepoBranchError && !deployRepoBranchError && !generatorBranchNameError;
        },
        resetAdvancedOptions: () => {
            setTemplateRepoBranch('');
            setDeployRepoBranch('template-poc'); // Reset to default value
            setGeneratorBranchName('main'); // Reset to default value
            setSkipBuildJobCreation(false);
        }
    }));

    if (layout === 'card') {
        return (
            <div className="generator-form">
                <div className="form-section" style={{ borderBottom: 'none', marginBottom: 0, paddingBottom: 0 }}>
                    <h3 className="form-section-title">
                        ⚙️ Advanced Options
                    </h3>

                    <div className="form-toggles" style={{ marginBottom: '16px' }}>
                        <div className="toggle-item">
                            <span className="toggle-text">CI/CD Pipeline</span>
                            <BaseSwitch
                                isChecked={!skipBuildJobCreation}
                                onChange={(checked) => handleSkipBuildJobCreationChange(!checked)}
                                label=""
                                id="cicd-pipeline-switch"
                                ouiaId="CICDPipelineSwitch"
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Template Branch</label>
                        <BaseInput
                            value={templateRepoBranch}
                            onChange={handleTemplateRepoBranchChange}
                            onBlur={handleTemplateRepoBranchBlur}
                            validated={templateRepoBranchError ? 'error' : 'default'}
                            isRequired={false}
                            placeholder="e.g., main"
                            ariaLabel="Template Repo Branch Input"
                            error={templateRepoBranchError}
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Deploy Branch</label>
                        <BaseInput
                            value={deployRepoBranch}
                            onChange={handleDeployRepoBranchChange}
                            onBlur={handleDeployRepoBranchBlur}
                            validated={deployRepoBranchError ? 'error' : 'default'}
                            isRequired={false}
                            placeholder="e.g., deploy"
                            ariaLabel="Deploy Repo Branch Input"
                            error={deployRepoBranchError}
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Generator Branch</label>
                        <BaseInput
                            value={generatorBranchName}
                            onChange={handleGeneratorBranchNameChange}
                            onBlur={handleGeneratorBranchNameBlur}
                            validated={generatorBranchNameError ? 'error' : 'default'}
                            isRequired={false}
                            placeholder="e.g., generator"
                            ariaLabel="Generator Branch Name Input"
                            error={generatorBranchNameError}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="form-group">
                <label className="form-label" style={{ fontWeight: 'bold', fontSize: '18px', cursor: 'pointer' }} onClick={() => setIsExpanded(!isExpanded)}>
                    Advanced Options {isExpanded ? '▲' : '▼'}
                </label>
            </div>
            {isExpanded && (
                <>
                    <div className="form-group">
                        <label className="form-label">Template Repo Branch</label>
                        <BaseInput
                            value={templateRepoBranch}
                            onChange={handleTemplateRepoBranchChange}
                            onBlur={handleTemplateRepoBranchBlur}
                            validated={templateRepoBranchError ? 'error' : 'default'}
                            isRequired={false}
                            placeholder="Enter Template Repo Branch"
                            ariaLabel="Template Repo Branch Input"
                            error={templateRepoBranchError}
                        />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Generator Branch Name</label>
                        <BaseInput
                            value={generatorBranchName}
                            onChange={handleGeneratorBranchNameChange}
                            onBlur={handleGeneratorBranchNameBlur}
                            validated={generatorBranchNameError ? 'error' : 'default'}
                            isRequired={false}
                            placeholder="Enter Generator Branch Name"
                            ariaLabel="Generator Branch Name Input"
                            error={generatorBranchNameError}
                        />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Deploy Repo Branch</label>
                        <BaseInput
                            value={deployRepoBranch}
                            onChange={handleDeployRepoBranchChange}
                            onBlur={handleDeployRepoBranchBlur}
                            validated={deployRepoBranchError ? 'error' : 'default'}
                            isRequired={false}
                            placeholder="Enter Deploy Repo Branch"
                            ariaLabel="Deploy Repo Branch Input"
                            error={deployRepoBranchError}
                        />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Skip Build Job Creation</label>
                        <Switch
                            id="skip-build-job-creation-switch"
                            label="Skip creating build job"
                            labelOff="Create build job"
                            isChecked={skipBuildJobCreation}
                            onChange={(_event, checked) => handleSkipBuildJobCreationChange(checked)}
                        />
                    </div>
                </>
            )}
        </>
    );
});

// Export a hook for the parent to access validation functions
export const useAdvancedOptionsValidation = () => {
    const advancedOptionsRef = React.useRef<AdvancedOptionsSectionRef>(null);

    const validateAdvancedOptions = React.useCallback(() => {
        return advancedOptionsRef.current?.validateAdvancedOptions() ?? true;
    }, []);

    const resetAdvancedOptions = React.useCallback(() => {
        advancedOptionsRef.current?.resetAdvancedOptions();
    }, []);

    return {
        advancedOptionsRef,
        validateAdvancedOptions,
        resetAdvancedOptions
    };
};
